import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { todayEasternDisplay, toEasternTime } from '../utils/time';

export default function DashboardPage() {
  const toast = useToast();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [posting, setPosting]   = useState(false);

  useEffect(() => {
    const fetchData = () => api.getDashboard()
      .then(setData)
      .catch(() => toast('Failed to load dashboard', 'error'))
      .finally(() => setLoading(false));
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, []);

  const triggerPost = async () => {
    setPosting(true);
    try {
      await api.triggerPost();
      toast('Post triggered! Check your Facebook Page in a moment.', 'success');
      const fresh = await api.getDashboard();
      setData(fresh);
    } catch (e) {
      toast(e.message || 'Failed to trigger post', 'error');
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  const d = data || {};
  const today = todayEasternDisplay({ weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginTop: 2 }}>{today}</p>
        </div>
        <button className="btn btn-teal" onClick={triggerPost} disabled={posting}>
          {posting ? 'Posting…' : '📝 Post Now'}
        </button>
      </div>

      {/* Stat cards — clickable */}
      <div className="stat-grid">
        <Link to="/posts" className={`stat-card ${d.posted_today ? 'success' : 'danger'}`} style={{textDecoration:'none'}}>
          <span className="stat-icon">{d.posted_today ? '✅' : '⏳'}</span>
          <span className="stat-value">{d.posted_today ? 'Done' : 'None'}</span>
          <span className="stat-label">Today's Post</span>
        </Link>
        <Link to="/escalations" className={`stat-card ${d.open_escalations > 0 ? 'danger' : ''}`} style={{textDecoration:'none'}}>
          <span className="stat-icon">🚨</span>
          <span className="stat-value">{d.open_escalations ?? 0}</span>
          <span className="stat-label">Open Escalations</span>
        </Link>
        <Link to="/leads" className={`stat-card ${d.new_leads > 0 ? 'gold' : ''}`} style={{textDecoration:'none'}}>
          <span className="stat-icon">⭐</span>
          <span className="stat-value">{d.new_leads ?? 0}</span>
          <span className="stat-label">New Leads</span>
        </Link>
        <Link to="/messages" className="stat-card" style={{textDecoration:'none'}}>
          <span className="stat-icon">💬</span>
          <span className="stat-value">{d.pending_comments ?? 0}</span>
          <span className="stat-label">Pending Comments</span>
        </Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Today's post preview */}
        <div className="card">
          <div className="card-header">
            <h3>Today's Post</h3>
            <Link to="/posts" className="btn btn-ghost btn-sm">All posts →</Link>
          </div>
          <div className="card-body">
            {d.todays_post ? (
              <>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 12 }}>
                  {d.todays_post.content}
                </p>
                <span className={`badge badge-${d.todays_post.status}`}>{d.todays_post.status}</span>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <p>No post yet today. Click "Post Now" to generate one.</p>
              </div>
            )}
          </div>
        </div>

        {/* Open escalations */}
        <div className="card">
          <div className="card-header">
            <h3>Open Escalations</h3>
            <Link to="/escalations" className="btn btn-ghost btn-sm">View all →</Link>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {d.recent_escalations?.length > 0 ? (
              <table>
                <tbody>
                  {d.recent_escalations.slice(0, 5).map(e => (
                    <tr key={e.escalation_id}>
                      <td>
                        <span className={`badge badge-${e.source_type}`}>{e.source_type}</span>
                      </td>
                      <td style={{ color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 600 }}>
                        {e.classification}
                      </td>
                      <td className="td-truncate" style={{ maxWidth: 180, fontSize: '0.82rem', color: 'var(--muted)' }}>
                        {e.content_summary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state" style={{ padding: '28px 20px' }}>
                <div className="empty-state-icon">✅</div>
                <p>No open escalations</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent audit */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <h3>Recent Activity</h3>
            <Link to="/audit" className="btn btn-ghost btn-sm">Full log →</Link>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Time</th><th>Action</th><th>Result</th><th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.recent_audit || []).map(a => (
                    <tr key={a.log_id}>
                      <td className="td-mono">{toEasternTime(a.timestamp)}</td>
                      <td style={{ fontSize: '0.82rem', fontWeight: 500 }}>{a.action_type}</td>
                      <td>
                        <span className={`badge ${a.success ? 'badge-published' : 'badge-failed'}`}>
                          {a.success ? 'OK' : 'FAIL'}
                        </span>
                      </td>
                      <td className="td-truncate" style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
                        {a.output_summary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.recent_audit?.length && (
                <div className="empty-state"><p>No activity yet today</p></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
